import React from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { ReactComponent as PageIcon } from "../../../../assets/img/personas/fermier.svg";
import { Trans } from "react-i18next";
import ActionButtons from "../ActionButtons";

const validationSchema = yup.object({
  idType: yup.string().required("Veuillez remplir ce champ"),
  idPrenom: yup
    .string()
    .required("Veuillez remplir ce champ")
    .trim("Veuillez remplir ce champ"),
  idNomDeFamille: yup
    .string()
    .required("Veuillez remplir ce champ")
    .trim("Veuillez remplir ce champ"),
  idSexe: yup.string().required("Veuillez remplir ce champ"),
  idDateDeNaissance: yup.string().required("Veuillez remplir ce champ"),
  idDeLaCarte: yup
    .string()
    .required("Veuillez remplir ce champ")
    .trim("Veuillez remplir ce champ"),
});

const PersonalInformation = (props) => {
  const [idError, setIdError] = React.useState(false);

  const formik = useFormik({
    initialValues: {
      ...{
        idPrenom: "",
        idNomDeFamille: "",
        idSexe: "",
        idType: "",
        idDateDeNaissance: "",
        idDeLaCarte: "",
      },
      ...props.data,
    },
    validationSchema,
    onSubmit: (vals) => {
      submit(vals);
    },
  });

  const submit = (vals) => {
    props.onSubmit(vals);
    // setUtils("loading", {
    //   spinning: true,
    //   primary: "Récupération De Vos Données",
    // });
  };

  return (
    <div className="px-4 sm:px-0">
      <div className="flex flex-col justify-center py-16">
        <div className="flex flex-col text-center items-center mb-10">
          <PageIcon />
          <div className="mt-9">
            <Typography variant="title">
              <Trans>formTitles.addProducerTitle</Trans>
            </Typography>
          </div>
        </div>

        <div className="mt-14">
          <Typography variant="title">
            <Trans>formTitles.farmersInformation</Trans>
          </Typography>
        </div>

        <form onSubmit={formik.handleSubmit}>
          <div className="mt-12 grid sm:grid-cols-2 gap-y-6 sm:gap-x-9">
            <div className="">
              <FormControl fullWidth>
                <TextField
                  name="idPrenom"
                  label="Prénom"
                  type="text"
                  value={formik.values.idPrenom}
                  error={
                    formik.touched.idPrenom && Boolean(formik.errors.idPrenom)
                  }
                  helperText={formik.touched.idPrenom && formik.errors.idPrenom}
                  onChange={formik.handleChange}
                />
              </FormControl>
            </div>
            <div className="">
              <FormControl fullWidth>
                <TextField
                  name="idNomDeFamille"
                  label="Nom de famille"
                  type="text"
                  value={formik.values.idNomDeFamille}
                  error={
                    formik.touched.idNomDeFamille &&
                    Boolean(formik.errors.idNomDeFamille)
                  }
                  helperText={
                    formik.touched.idNomDeFamille &&
                    formik.errors.idNomDeFamille
                  }
                  onChange={formik.handleChange}
                />
              </FormControl>
            </div>

            <div className="">
              <FormControl
                fullWidth
                error={formik.touched.idSexe && Boolean(formik.errors.idSexe)}
              >
                <InputLabel>Sexe</InputLabel>
                <Select
                  name="idSexe"
                  label="Sexe"
                  value={formik.values.idSexe}
                  onChange={formik.handleChange}
                >
                  {[
                    { label: "Masculin", value: "MALE" },
                    { label: "Féminin", value: "FEMALE" },
                  ].map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {formik.touched.idSexe && formik.errors.idSexe}
                </FormHelperText>
              </FormControl>
            </div>
            <div className="">
              <FormControl
                fullWidth
                error={
                  formik.touched.idDateDeNaissance &&
                  Boolean(formik.errors.idDateDeNaissance)
                }
              >
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale="fr"
                >
                  <DatePicker
                    renderInput={(props) => <TextField {...props} />}
                    name="idDateDeNaissance"
                    label="Date de Naissance"
                    inputFormat="MM/DD/YYYY"
                    // maxDate={new Date()}
                    disableFuture={true}
                    error={
                      formik.touched.idDateDeNaissance &&
                      Boolean(formik.errors.idDateDeNaissance)
                    }
                    helperText={
                      formik.touched.idDateDeNaissance &&
                      formik.errors.idDateDeNaissance
                    }
                    value={formik.values.idDateDeNaissance}
                    onChange={(v) => {
                      const selectedDate = dayjs(v);
                      if (selectedDate.isValid()) {
                        const formattedDate = selectedDate.format("MM/DD/YYYY");
                        formik.setFieldValue(
                          "idDateDeNaissance",
                          formattedDate
                        );
                      } else {
                        // Handle the case where the selected date is not valid
                        console.error("Invalid date selected");
                      }
                    }}
                  />
                </LocalizationProvider>
              </FormControl>
            </div>

            <div className="">
              <FormControl
                fullWidth
                error={formik.touched.idType && Boolean(formik.errors.idType)}
              >
                <InputLabel>Type de pièce d’identité</InputLabel>
                <Select
                  name="idType"
                  label="Type de pièce d’identité"
                  placeholder="Sélectionner"
                  value={formik.values.idType}
                  onChange={formik.handleChange}
                >
                  {[
                    {
                      label: "Carte d'Idenité Nationale",
                      value: "NATIONAL_ID",
                    },
                    {
                      label: "Certificat d'Identification Personnelle",
                      value: "PASSPORT",
                    },
                    { label: "Carte Lépi", value: "LEPI" },
                    { label: "Carte NPI", value: "NPI" },
                    { label: "RAVIP", value: "RAVIP" },
                    { label: "Carte biométrique", value: "FARMER_ID" },
                  ].map((arr) => (
                    <MenuItem key={arr.label} value={arr.value}>
                      {arr.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {formik.touched.idType && formik.errors.idType}
                </FormHelperText>
              </FormControl>
            </div>
            <div className="">
              <FormControl fullWidth>
                <TextField
                  name="idDeLaCarte"
                  label="N° de la carte"
                  type="text"
                  value={formik.values.idDeLaCarte.trim()}
                  error={
                    formik.touched.idDeLaCarte &&
                    Boolean(formik.errors.idDeLaCarte)
                  }
                  helperText={
                    formik.touched.idDeLaCarte && formik.errors.idDeLaCarte
                  }
                  onChange={formik.handleChange}
                />
              </FormControl>
            </div>
          </div>

          <div className="flex justify-end mt-20 gap-6">
            <ActionButtons onNextStep={formik.handleSubmit} />
          </div>
        </form>
      </div>
    </div>
  );
};

export default PersonalInformation;
